import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Button,
  ButtonColorOption,
  ButtonVariantOption,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonColorOption?: ButtonColorOption;
  cancelButtonColorOption?: ButtonColorOption;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * 操作を実行する前に確認を求めるダイアログ（削除・解除など）。
 * 背景のクリックやEscキーでは閉じず、キャンセルか確定のボタンでのみ閉じる。`message` 内の改行（`\n`）はそのまま改行として表示する。
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = "確認",
  cancelLabel = "キャンセル",
  confirmButtonColorOption = ButtonColorOption.Primary,
  cancelButtonColorOption = ButtonColorOption.Primary,
  onConfirm,
  onCancel,
}) => {
  const handleClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-confirm-dialog-title"
      aria-describedby="delete-donfirm-dialog-description"
    >
      <DialogTitle id="delete-confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        {/* message 内の改行（\n）をそのまま改行として表示する */}
        <DialogContentText
          id="delete-donfirm-dialog-description"
          sx={{ whiteSpace: "pre-line" }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
        <Button
          onClick={onCancel}
          variant={ButtonVariantOption.Outlined}
          color={cancelButtonColorOption}
          size={SizingWrapperStyle.SMALL}
          label={cancelLabel}
        />
        <Button
          onClick={onConfirm}
          variant={ButtonVariantOption.Contained}
          color={confirmButtonColorOption}
          size={SizingWrapperStyle.SMALL}
          label={confirmLabel}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
